import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Input, Modal, Popconfirm, Spin, Table, Tag, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import { protectedApi } from "../../../app/utils/common/protectedApi";
import { APIS } from "../../../app/utils/constants/apiConstant";
import {
  checkPermission,
  hasAdminAccess,
  permissionType,
  showErrorResponse,
} from "../../../app/utils/common/Functions";
import { showErrorToast, showSuccessToast } from "../../../app/utils/common/Toast";
import {
  downloadBlob,
  fetchOffboardingSubmissionBlob,
  fetchOffboardingTemplateBlob,
} from "../../../app/utils/common/offboardingDocumentFile";
import PageContainerHeader from "../../molecules/PageContainerHeader/PageContainerHeader";
import EmployeeSelect from "../../atoms/EmployeeSelect/EmployeeSelect";
import OffboardingDocxViewer from "../../molecules/OffboardingDocxViewer/OffboardingDocxViewer";
import { useSelector } from "react-redux";

const { Text, Title } = Typography;

const statusTag = (status) => {
  if (status === "submitted") {
    return <Tag color="green">Submitted</Tag>;
  }
  return <Tag color="orange">Pending</Tag>;
};

const OffboardingDocumentsPage = () => {
  const { user } = useSelector((state) => state.auth.auth);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [canManageAll, setCanManageAll] = useState(false);
  const [canAssign, setCanAssign] = useState(false);

  const [title, setTitle] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assigningDoc, setAssigningDoc] = useState(null);
  const [assignUserIds, setAssignUserIds] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);

  const [submissionsModalOpen, setSubmissionsModalOpen] = useState(false);
  const [submissionsDoc, setSubmissionsDoc] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submittingAssignment, setSubmittingAssignment] = useState(null);
  const [submitFile, setSubmitFile] = useState(null);
  const [submitSaving, setSubmitSaving] = useState(false);
  const submitFileInputRef = useRef(null);

  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [downloadLoadingKey, setDownloadLoadingKey] = useState(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [viewMode, setViewMode] = useState("submission");

  const canRead =
    hasAdminAccess(user) || checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "read");
  const canCreate =
    hasAdminAccess(user) || checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "create");
  const canDelete =
    hasAdminAccess(user) || checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "delete");
  const canAssignEmployees =
    hasAdminAccess(user) ||
    checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "assign employee");
  const canViewSubmissionFiles =
    hasAdminAccess(user) ||
    checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "view submissions");
  const canSubmitDocument =
    hasAdminAccess(user) || checkPermission(permissionType.OFFBOARDING_DOCUMENTS, "submit");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await protectedApi({
        url: APIS.OFFBOARDING_DOCUMENTS,
        method: "GET",
      });
      const data = response?.data?.data;
      setDocuments(Array.isArray(data?.documents) ? data.documents : []);
      setAssignments(Array.isArray(data?.assignments) ? data.assignments : []);
      setCanManageAll(Boolean(data?.canManageAll));
      setCanAssign(Boolean(data?.canAssign));
    } catch (error) {
      showErrorResponse(error);
      setDocuments([]);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canRead) loadData();
  }, [canRead, loadData]);

  const handleFilePick = (event) => {
    setUploadFile(event.target.files?.[0] || null);
  };

  const handleUpload = async () => {
    if (!canCreate) return;
    const safeTitle = title.trim();
    if (!safeTitle) {
      showErrorToast("Enter a document title.");
      return;
    }
    if (!uploadFile) {
      showErrorToast("Choose a .docx file to upload.");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", safeTitle);
      if (canAssignEmployees && assignedUserIds.length) {
        formData.append("assignedUserIds", JSON.stringify(assignedUserIds));
      }
      await protectedApi({
        url: APIS.OFFBOARDING_DOCUMENTS,
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        _dataEncrypted: true,
      });
      showSuccessToast("Offboarding document uploaded.");
      setTitle("");
      setUploadFile(null);
      setAssignedUserIds([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadData();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async (documentId, fileName) => {
    if (!documentId) return;
    setDownloadLoadingKey(`template-${documentId}`);
    try {
      const blob = await fetchOffboardingTemplateBlob(documentId);
      downloadBlob(blob, fileName || "offboarding-template.docx");
    } catch (error) {
      showErrorToast(error?.message || "Unable to download template.");
    } finally {
      setDownloadLoadingKey(null);
    }
  };

  const handleDownloadSubmission = async (assignment) => {
    if (!assignment?.assignmentId) return;
    setDownloadLoadingKey(`submission-${assignment.assignmentId}`);
    try {
      const blob = await fetchOffboardingSubmissionBlob(assignment.assignmentId);
      downloadBlob(blob, assignment.submissionFileName || "offboarding-submission.docx");
    } catch (error) {
      showErrorToast(error?.message || "Unable to download submission.");
    } finally {
      setDownloadLoadingKey(null);
    }
  };

  const openDocumentView = (item, mode = "submission") => {
    setViewItem(item);
    setViewMode(mode);
    setViewOpen(true);
  };

  const closeDocumentView = () => {
    setViewOpen(false);
    setViewItem(null);
  };

  const openAssignModal = async (doc) => {
    if (!canAssignEmployees || !doc?.id) return;
    setAssigningDoc(doc);
    setAssignModalOpen(true);
    setAssignLoading(true);
    setAssignUserIds([]);
    try {
      const response = await protectedApi({
        url: APIS.OFFBOARDING_DOCUMENT_BY_ID.replace(":documentId", String(doc.id)),
        method: "GET",
      });
      const assigned = response?.data?.data?.assignments;
      setAssignUserIds(
        Array.isArray(assigned) ? assigned.map((u) => u.userId) : []
      );
    } catch (error) {
      showErrorResponse(error);
      setAssignModalOpen(false);
      setAssigningDoc(null);
    } finally {
      setAssignLoading(false);
    }
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setAssigningDoc(null);
    setAssignUserIds([]);
  };

  const saveAssignments = async () => {
    if (!assigningDoc?.id) return;
    setAssignSaving(true);
    try {
      await protectedApi({
        url: APIS.OFFBOARDING_DOCUMENT_ASSIGNMENTS.replace(
          ":documentId",
          String(assigningDoc.id)
        ),
        method: "PUT",
        data: { userIds: assignUserIds },
      });
      showSuccessToast("Assignments updated.");
      closeAssignModal();
      await loadData();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setAssignSaving(false);
    }
  };

  const openSubmissionsModal = async (doc) => {
    if (!doc?.id) return;
    setSubmissionsDoc(doc);
    setSubmissionsModalOpen(true);
    setSubmissionsLoading(true);
    setSubmissions([]);
    try {
      const response = await protectedApi({
        url: APIS.OFFBOARDING_DOCUMENT_BY_ID.replace(":documentId", String(doc.id)),
        method: "GET",
      });
      setSubmissions(Array.isArray(response?.data?.data?.assignments) ? response.data.data.assignments : []);
    } catch (error) {
      showErrorResponse(error);
      setSubmissionsModalOpen(false);
      setSubmissionsDoc(null);
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const closeSubmissionsModal = () => {
    setSubmissionsModalOpen(false);
    setSubmissionsDoc(null);
    setSubmissions([]);
  };

  const handleViewSubmission = (assignment) => {
    if (!assignment?.assignmentId) return;
    const enriched = {
      ...assignment,
      title: assignment.title || submissionsDoc?.title,
    };
    if (submissionsModalOpen) {
      closeSubmissionsModal();
    }
    openDocumentView(enriched, "submission");
  };

  const openSubmitModal = (assignment) => {
    if (!canSubmitDocument || assignment?.status === "submitted") return;
    setSubmittingAssignment(assignment);
    setSubmitFile(null);
    setSubmitModalOpen(true);
    if (submitFileInputRef.current) submitFileInputRef.current.value = "";
  };

  const closeSubmitModal = () => {
    setSubmitModalOpen(false);
    setSubmittingAssignment(null);
    setSubmitFile(null);
  };

  const handleSubmitDocument = async () => {
    if (!submittingAssignment?.assignmentId) return;
    if (!submitFile) {
      showErrorToast("Choose your filled .docx file.");
      return;
    }
    setSubmitSaving(true);
    try {
      const formData = new FormData();
      formData.append("file", submitFile);
      await protectedApi({
        url: APIS.OFFBOARDING_DOCUMENT_SUBMIT.replace(
          ":assignmentId",
          String(submittingAssignment.assignmentId)
        ),
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        _dataEncrypted: true,
      });
      showSuccessToast("Offboarding document submitted.");
      closeSubmitModal();
      await loadData();
      openDocumentView(
        {
          ...submittingAssignment,
          status: "submitted",
          submissionFileName: submitFile.name,
        },
        "submission"
      );
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setSubmitSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!canDelete || !doc?.id) return;
    setDeleteLoadingId(doc.id);
    try {
      await protectedApi({
        url: APIS.OFFBOARDING_DOCUMENT_BY_ID.replace(":documentId", String(doc.id)),
        method: "DELETE",
      });
      showSuccessToast("Offboarding document removed.");
      await loadData();
    } catch (error) {
      showErrorResponse(error);
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const managerColumns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (value) => <span className="font-medium text-[#1f2b44]">{value}</span>,
    },
    {
      title: "Template",
      dataIndex: "fileName",
      key: "fileName",
      render: (value) => value || "—",
    },
    {
      title: "Assigned",
      dataIndex: "assignedCount",
      key: "assignedCount",
      width: 110,
      render: (count) =>
        count > 0 ? (
          <Tag color="blue">
            {count} employee{count === 1 ? "" : "s"}
          </Tag>
        ) : (
          <Tag>Unassigned</Tag>
        ),
    },
    {
      title: "Submitted",
      key: "submittedCount",
      width: 110,
      render: (_, row) => (
        <Tag color={row.submittedCount > 0 ? "green" : "default"}>
          {row.submittedCount || 0} / {row.assignedCount || 0}
        </Tag>
      ),
    },
    {
      title: "Uploaded",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (value) => (value ? moment(value).format("D MMM YYYY, h:mm A") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 360,
      render: (_, row) => (
        <div className="flex flex-wrap gap-2">
          <Button
            size="small"
            onClick={() => openDocumentView({ ...row, documentId: row.id }, "template")}
          >
            View
          </Button>
          <Button
            size="small"
            loading={downloadLoadingKey === `template-${row.id}`}
            onClick={() => handleDownloadTemplate(row.id, row.fileName)}
          >
            Download
          </Button>
          {canAssignEmployees ? (
            <Button size="small" onClick={() => openAssignModal(row)}>
              Assign
            </Button>
          ) : null}
          {canViewSubmissionFiles ? (
            <Button size="small" onClick={() => openSubmissionsModal(row)}>
              Submissions
            </Button>
          ) : null}
          {canDelete ? (
            <Popconfirm
              title="Delete this offboarding document?"
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(row)}
            >
              <Button size="small" danger loading={deleteLoadingId === row.id}>
                Delete
              </Button>
            </Popconfirm>
          ) : null}
        </div>
      ),
    },
  ];

  const employeeColumns = [
    {
      title: "Document",
      dataIndex: "title",
      key: "title",
      render: (value) => <span className="font-medium text-[#1f2b44]">{value}</span>,
    },
    {
      title: "Template",
      dataIndex: "fileName",
      key: "fileName",
      render: (value) => value || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (value) => statusTag(value),
    },
    {
      title: "Submitted",
      dataIndex: "submittedAt",
      key: "submittedAt",
      width: 180,
      render: (value) => (value ? moment(value).format("D MMM YYYY, h:mm A") : "—"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, row) => (
        <div className="flex flex-wrap gap-2">
          <Button
            size="small"
            onClick={() => openDocumentView({ ...row, documentId: row.documentId }, "template")}
          >
            View template
          </Button>
          <Button
            size="small"
            loading={downloadLoadingKey === `template-${row.documentId}`}
            onClick={() => handleDownloadTemplate(row.documentId, row.fileName)}
          >
            Download template
          </Button>
          {row.status === "submitted" ? (
            <Button size="small" type="primary" onClick={() => handleViewSubmission(row)}>
              View submission
            </Button>
          ) : canSubmitDocument ? (
            <Button size="small" type="primary" onClick={() => openSubmitModal(row)}>
              Submit
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  if (!canRead) {
    return (
      <div className="p-6">
        <Text type="danger">You do not have permission to view offboarding documents.</Text>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col gap-6">
      <PageContainerHeader title="Offboarding Documents" />

      <p className="text-sm text-gray-600 -mt-2">
        {canManageAll
          ? "Upload a .docx offboarding template, assign it to employees, and review their submitted documents."
          : "Download the assigned template, fill it in Word, then upload and submit your completed document."}
      </p>

      {canCreate ? (
        <div className="border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm flex flex-col gap-4 max-w-3xl">
          <Title level={5} className="!mb-0">
            Upload offboarding template
          </Title>
          <div>
            <label className="block text-sm font-semibold text-[#202b44] mb-2">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Exit clearance form"
              maxLength={500}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#202b44] mb-2">
              Word document (.docx)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleFilePick}
            />
            {uploadFile ? (
              <Text type="secondary" className="block mt-1 text-sm">
                Selected: {uploadFile.name}
              </Text>
            ) : null}
          </div>
          {canAssignEmployees ? (
            <div>
              <label className="block text-sm font-semibold text-[#202b44] mb-2">
                Assign to employees (optional)
              </label>
              <EmployeeSelect
                isMultiple
                value={assignedUserIds}
                onSelectEmployee={setAssignedUserIds}
                width="100%"
                placeholder="Select employees..."
                excludeCeoCmo={false}
              />
            </div>
          ) : null}
          <div>
            <Button type="primary" loading={uploading} onClick={handleUpload}>
              Upload document
            </Button>
          </div>
        </div>
      ) : null}

      <div className="border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm">
        <Title level={5} className="!mb-4">
          {canManageAll ? "All offboarding documents" : "My offboarding documents"}
        </Title>
        <Table
          rowKey={canManageAll ? "id" : "assignmentId"}
          loading={loading}
          dataSource={canManageAll ? documents : assignments}
          columns={canManageAll ? managerColumns : employeeColumns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText: canManageAll
              ? "No offboarding documents uploaded yet."
              : "No offboarding documents have been assigned to you yet.",
          }}
          scroll={{ x: 720 }}
        />
      </div>

      <Modal
        title={assigningDoc ? `Assign: ${assigningDoc.title}` : "Assign document"}
        open={assignModalOpen}
        onCancel={closeAssignModal}
        onOk={saveAssignments}
        okText="Save assignments"
        confirmLoading={assignSaving}
        destroyOnClose
      >
        {assignLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : (
          <div className="py-2">
            <label className="block text-sm font-semibold text-[#202b44] mb-2">
              Employees
            </label>
            <EmployeeSelect
              isMultiple
              value={assignUserIds}
              onSelectEmployee={setAssignUserIds}
              width="100%"
              placeholder="Select employees to assign this document..."
              excludeCeoCmo={false}
            />
          </div>
        )}
      </Modal>

      <Modal
        title={submissionsDoc ? `Submissions: ${submissionsDoc.title}` : "Submissions"}
        open={submissionsModalOpen}
        onCancel={closeSubmissionsModal}
        footer={null}
        width={720}
        destroyOnClose
      >
        {submissionsLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : (
          <Table
            rowKey="assignmentId"
            dataSource={submissions}
            pagination={false}
            locale={{ emptyText: "No employees assigned yet." }}
            columns={[
              {
                title: "Employee",
                dataIndex: "name",
                key: "name",
              },
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (value) => statusTag(value),
              },
              {
                title: "Submitted",
                dataIndex: "submittedAt",
                key: "submittedAt",
                render: (value) => (value ? moment(value).format("D MMM YYYY, h:mm A") : "—"),
              },
              {
                title: "Action",
                key: "action",
                render: (_, row) =>
                  row.status === "submitted" ? (
                    <Button
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleViewSubmission(row);
                      }}
                    >
                      View
                    </Button>
                  ) : (
                    "—"
                  ),
              },
            ]}
          />
        )}
      </Modal>

      <Modal
        title={submittingAssignment ? `Submit: ${submittingAssignment.title}` : "Submit document"}
        open={submitModalOpen}
        onCancel={closeSubmitModal}
        onOk={handleSubmitDocument}
        okText="Submit document"
        confirmLoading={submitSaving}
        destroyOnClose
      >
        <div className="flex flex-col gap-4 py-2">
          <Text type="secondary">
            Download the template, fill it in Microsoft Word, then upload your completed .docx file
            below.
          </Text>
          <Button
            onClick={() =>
              handleDownloadTemplate(
                submittingAssignment?.documentId,
                submittingAssignment?.fileName
              )
            }
            loading={downloadLoadingKey === `template-${submittingAssignment?.documentId}`}
          >
            Download template
          </Button>
          <div>
            <label className="block text-sm font-semibold text-[#202b44] mb-2">
              Filled document (.docx)
            </label>
            <input
              ref={submitFileInputRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setSubmitFile(e.target.files?.[0] || null)}
            />
            {submitFile ? (
              <Text type="secondary" className="block mt-1 text-sm">
                Selected: {submitFile.name}
              </Text>
            ) : null}
          </div>
        </div>
      </Modal>

      {viewOpen && viewItem
        ? createPortal(
            <div
              className="project-doc-view-shell fixed inset-0 bg-[#eef2f9] overflow-y-auto"
              style={{ zIndex: 2000 }}
            >
              <div className="h-auto mx-auto px-4 py-6 sm:px-6">
                <div className="project-doc-view-panel border border-solid border-[#e7ebf6] rounded-[14px] p-6 bg-white shadow-sm min-h-[calc(100vh-3rem)] flex flex-col">
                  <div className="project-doc-view-header flex flex-wrap items-start justify-between gap-3 mb-4 shrink-0">
                    <div className="min-w-0 flex-1">
                      <h1 className="page-title mb-1 break-words">
                        {viewMode === "submission"
                          ? viewItem.title || "Submitted document"
                          : viewItem.title || "Offboarding template"}
                      </h1>
                      <p className="text-sm text-[#4e5d78] mb-1">
                        {viewMode === "submission" ? (
                          <>
                            <span className="font-semibold">Submitted:</span>{" "}
                            {viewItem.submittedAt
                              ? moment(viewItem.submittedAt).format("D MMM YYYY, h:mm A")
                              : "—"}
                            {viewItem.name ? (
                              <>
                                {" "}
                                · <span className="font-semibold">Employee:</span> {viewItem.name}
                              </>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <span className="font-semibold">Template:</span>{" "}
                            {viewItem.fileName || "—"}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 shrink-0">
                      <Button
                        icon={<DownloadOutlined />}
                        loading={
                          viewMode === "submission"
                            ? downloadLoadingKey === `submission-${viewItem.assignmentId}`
                            : downloadLoadingKey === `template-${viewItem.documentId || viewItem.id}`
                        }
                        onClick={() =>
                          viewMode === "submission"
                            ? handleDownloadSubmission(viewItem)
                            : handleDownloadTemplate(
                                viewItem.documentId || viewItem.id,
                                viewItem.fileName
                              )
                        }
                      >
                        Download
                      </Button>
                      <Button onClick={closeDocumentView}>Close</Button>
                    </div>
                  </div>

                  <div className="flex-1 min-h-0">
                    <div className="h-full flex flex-col min-h-[calc(100vh-220px)]">
                      <p className="text-sm font-semibold text-[#1f2b44] mb-2 shrink-0">
                        {viewMode === "submission"
                          ? `Submitted document${viewItem.submissionFileName ? `: ${viewItem.submissionFileName}` : ""}`
                          : `Word document${viewItem.fileName ? `: ${viewItem.fileName}` : ""}`}
                      </p>
                      <div className="flex-1 overflow-hidden rounded-[10px]">
                        <OffboardingDocxViewer
                          assignmentId={
                            viewMode === "submission" ? viewItem.assignmentId : undefined
                          }
                          documentId={
                            viewMode === "template"
                              ? viewItem.documentId || viewItem.id
                              : undefined
                          }
                          onLoadError={() =>
                            showErrorToast(
                              "Could not display the Word document. Try download or contact support."
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default OffboardingDocumentsPage;
